import { useEffect, useMemo, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import DefinitionList from '../components/DefinitionList';
import LifeRail from '../components/LifeRail';
import Pagination, { usePagination } from '../components/Pagination';
import { fmtDate, fmtDT } from '../utils/format';

function typedFromExisting(existing, value) {
  // Coerce back toward the existing value's type so a boolean/number field
  // doesn't silently become a string on save.
  if (typeof existing === 'boolean') return value === 'true';
  if (typeof existing === 'number') return value === '' ? existing : Number(value);
  return value;
}

// Same five EmployeeEnrolment.Status values as EmployerContext's
// OutstandingView (BrandonUserStoryAPI.md's `enrolment_status` filter) —
// "all" is this view's own default (unfiltered ICIB-01a), not one of the
// documented values, and maps to skipping the filter entirely.
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'invited', label: 'Invited' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'onboarding_unsuccessful', label: 'Onboarding unsuccessful' },
  { value: 'removed', label: 'Removed' },
  { value: 'archived', label: 'Archived' },
];

/* ICIB-01 — View all employees covered under an employer scheme.
   Two operations, deliberately split (§ICIB-01): a lightweight list
   across the whole group (ICIB-01a, GET .../employees/summary —
   employee_id + user only, no form_data/enrolments), and a per-person
   drill-down (ICIB-01b, GET .../employees/{id}) for the rest. The table
   below is 01a; opening a row fetches 01b on demand (see
   IcibAdminContext#loadEmployeeDetail) rather than paying for every
   person's full record just to render the register. */
function EmployeesView() {
  const {
    status, groupId, curGroup, employeesIn, product, saveFormData, employeeDetails, loadEmployeeDetail,
    employeesStatus, employeesError, loadEmployees,
    filteredEmployeesIn, filteredEmployeesStatus, filteredEmployeesError, loadFilteredEmployees,
  } = useIcibAdmin();
  const { openDrawer } = useDrawer();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [enrolmentStatus, setEnrolmentStatus] = useState('all');
  const filtering = enrolmentStatus !== 'all';

  useEffect(() => { loadEmployees(groupId); }, [groupId, loadEmployees]);
  useEffect(() => {
    if (filtering) loadFilteredEmployees(groupId, enrolmentStatus);
  }, [groupId, enrolmentStatus, filtering, loadFilteredEmployees]);

  const listStatus = filtering ? filteredEmployeesStatus : employeesStatus;
  const listError = filtering ? filteredEmployeesError : employeesError;

  const employees = useMemo(() => {
    const list = !groupId ? [] : filtering ? filteredEmployeesIn(groupId) : employeesIn(groupId);
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((emp) => `${emp.user?.first_name || ''} ${emp.user?.last_name || ''} ${emp.user?.email || ''}`.toLowerCase().includes(needle));
  }, [groupId, filtering, filteredEmployeesIn, employeesIn, q]);
  const { page, setPage, pageCount, pageItems: pagedEmployees, total } = usePagination(employees);

  async function onSaveFormData(emp, patch) {
    const res = await saveFormData(emp.id, patch);
    if (res?.error) toast(res.message || 'Could not save this override.', 'bad', res.error);
    else { toast('form_data updated — recorded as an admin override.', 'ok'); loadEmployeeDetail(groupId, emp.id); }
    return res;
  }

  function open(emp) {
    openDrawer(
      `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.user?.email,
      `ICIB-01b · ${emp.id}`,
      <EmployeeDrawer gid={groupId} emp={emp} product={product} onSaveFormData={onSaveFormData} />
    );
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Membership register</div>
      <div className="page-h"><h2>Employees</h2></div>
      <p className="page-sub">Everyone covered under {curGroup()?.name || 'this employer scheme'} — who is in it. Open a person for their enrolments and submitted details.</p>

      {listError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load the register for this company</b>{listError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      ) : (
        <>
          <div className="filterbar">
            <select value={enrolmentStatus} onChange={(e) => setEnrolmentStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <input type="search" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="spacer" style={{ flex: 1 }} />
            <span className="ep">GET /companies/{'{company_id}'}/employees/summary{filtering ? `?enrolment_status=${enrolmentStatus}` : ''}</span>
          </div>

          <div className="card">
            {listStatus === 'loading' || listStatus === 'idle' ? (
              <div className="empty"><b>Loading register…</b></div>
            ) : employees.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Person</th><th></th></tr></thead>
                  <tbody>
                    {pagedEmployees.map((emp) => {
                      const detail = employeeDetails[emp.id];
                      return (
                        <tr className="clickable" key={emp.id} onClick={() => open(emp)}>
                          <td>
                            <span className="strong">{emp.user?.first_name} {emp.user?.last_name}</span><br />
                            <span className="mono dim">{emp.user?.email}</span>
                          </td>
                          <td>
                            {detail ? (detail.enrolments || []).length
                              ? <span className="dim">{detail.enrolments.length} enrolment(s)</span>
                              : <span className="dim">No enrolments</span>
                              : <button className="btn sm" onClick={(e) => { e.stopPropagation(); open(emp); }}>Open</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty"><b>No matches</b>{q
                ? 'Try a different search.'
                : filtering
                  ? `Nobody currently has an enrolment in "${STATUS_OPTIONS.find((o) => o.value === enrolmentStatus)?.label}" status.`
                  : 'No one is enrolled in this company yet — see Enrolment.'}</div>
            )}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </>
      )}
    </>
  );
}

function EmployeeDrawer({ gid, emp, product, onSaveFormData }) {
  const { employeeDetails, loadEmployeeDetail, activeTemplates, loadActiveTemplate, loadEmployees } = useIcibAdmin();
  // ICIB-01b — set when the detail fetch fails (e.g. 404 because the
  // employee was removed after the list loaded). Shown instead of an
  // endless "Loading…"; on 404 the list is reloaded so the stale row goes.
  const [loadError, setLoadError] = useState(null);
  // loadEmployeeDetail isn't stable across renders (IcibAdminContext
  // doesn't memoize it) — key off emp.id alone, same pattern as
  // FormsView's TemplateDrawer.
  useEffect(() => {
    let live = true;
    setLoadError(null);
    loadEmployeeDetail(gid, emp.id).then((res) => {
      if (!live || !res?.error) return;
      setLoadError(res);
      if (res.error === 'not_found') loadEmployees(gid);
    });
    return () => { live = false; };
  }, [gid, emp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Same active-template fetch IntakeView's AdditionPanel uses — needed
  // here too so a dropdown-type field (e.g. nz_citizen_or_visa, §ICIB-10b)
  // renders its fixed `options` on the override form below instead of a
  // free-text box that could save a value the template doesn't allow.
  useEffect(() => { if (gid) loadActiveTemplate(gid); }, [gid, loadActiveTemplate]);
  const detail = employeeDetails[emp.id];
  const tplFields = activeTemplates[gid]?.fields;

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Contact</h3></div>
      <DefinitionList rows={[
        ['Email', emp.user?.email || '—'],
        ['Date of birth', fmtDate(emp.user?.dob)],
        ['Phone', emp.user?.phone || '—'],
        ['Address', emp.user?.address || '—'],
        ['Gender', emp.user?.gender || '—'],
      ]} />

      {loadError ? (
        <div className="note bad" style={{ marginTop: 14 }}><b>{loadError.error === 'not_found' ? 'This employee no longer exists' : "Couldn't load this employee"}</b>{loadError.message || 'Please reload the list.'}</div>
      ) : !detail ? (
        <div className="empty"><b>Loading…</b></div>
      ) : (
        <>
          {/* ICIB-01b's enrolment objects (BrandonUserStoryAPI.md
              §ICIB-01b) also carry reason, effective_date, removal_reason
              and removal_effective_date alongside enrolment_id/product_id/
              status/invited_at — onboarding_updated_at/removed_at/
              archived_at still aren't part of this shape, or of ICIB-03b's;
              those three timestamps only ever appear in the single-item
              action's own response, at the moment it happens (removed_at
              on ICIB-02c §Removal, onboarding_updated_at on ICIB-04a
              §Onboarding, archived_at on ICIB-04c §confirm removal) — no
              GET endpoint persists or re-serves them afterwards. */}
          <div className="sec-h" style={{ marginTop: 0 }}><h3>Enrolments</h3></div>
          {(detail.enrolments || []).length ? detail.enrolments.map((en) => (
            <div className="card card-b" key={en.id} style={{ marginBottom: 10 }}>
              <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 8 }}>
                <b>{product(en.product_id)?.name || en.product_id}</b>
                <span className="spacer" />
                <LifeRail status={en.status} withLabel />
              </div>
              <DefinitionList rows={[
                ['Invited', fmtDT(en.invited_at)],
                ['Employment effective date', fmtDate(en.effective_date)],
                ['Reason', en.reason || '—'],
                ['Removal reason', en.removal_reason || '—'],
                ['Removal effective date', fmtDate(en.removal_effective_date)],
              ]} />
            </div>
          )) : <div className="empty"><b>No enrolments</b></div>}

          <div className="sec-h"><h3>Company-Specific extra details</h3><span className="spacer" /><span className="ep">PATCH .../form-data</span></div>
          <FormDataEditor emp={emp} detail={detail} tplFields={tplFields} onSave={onSaveFormData} />
        </>
      )}
    </>
  );
}

// ICIB-02d — the only path that can write Employee.form_data after intake.
// Edits existing keys only (no self-service add-a-new-key). `tplFields`
// (the group's active template, when loaded) lets a dropdown-type field —
// e.g. nz_citizen_or_visa, fixed to ["Yes", "No"] per §ICIB-10b — render
// its actual `options` here too, instead of a free-text box that could
// save a value the template wouldn't otherwise allow; falls back to
// inferring from the existing value's type for any key the template
// hasn't told us about yet (still loading, or a field since removed).
function FormDataEditor({ emp, detail, tplFields, onSave }) {
  const original = detail.form_data || {};
  const [values, setValues] = useState(() => ({ ...original }));
  const [busy, setBusy] = useState(false);
  const entries = Object.entries(original);
  const fieldByKey = (k) => tplFields?.find((f) => f.field_key === k);

  async function submit(e) {
    e.preventDefault();
    const patch = {};
    for (const [k, v] of Object.entries(values)) {
      if (v !== original[k]) patch[k] = typedFromExisting(original[k], v);
    }
    if (!Object.keys(patch).length) return;
    setBusy(true);
    const res = await onSave(emp, patch);
    setBusy(false);
    if (!res?.error) setValues((cur) => ({ ...cur }));
  }

  if (!entries.length) return <div className="empty"><b>No company-specific extra details</b></div>;

  return (
    <form onSubmit={submit}>
      {entries.map(([k, v]) => {
        const field = fieldByKey(k);
        return (
          <div className="field" key={k}>
            <label>{field?.label || k.replace(/_/g, ' ')}</label>
            {field?.field_type === 'dropdown' ? (
              <select value={values[k] ?? ''} onChange={(e) => setValues((cur) => ({ ...cur, [k]: e.target.value }))}>
                {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : typeof v === 'boolean' ? (
              <select value={String(values[k])} onChange={(e) => setValues((cur) => ({ ...cur, [k]: e.target.value === 'true' }))}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <input value={values[k] ?? ''} onChange={(e) => setValues((cur) => ({ ...cur, [k]: e.target.value }))} />
            )}
          </div>
        );
      })}
      <div className="hint" style={{ marginBottom: 10 }}>Only changed keys are sent — the rest of form_data is left untouched. Recorded as an admin override (updated_by/updated_at).</div>
      <div className="btn-row"><button type="submit" className="btn pri sm" disabled={busy}>{busy ? 'Saving…' : 'Save overrides'}</button></div>
    </form>
  );
}

export default EmployeesView;
